import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import EntryFormHeader from '../../components/header/EntryFormHeader';
import { RootState } from '../../redux/store';
import { setField } from '../../redux/entryFormSlice';
import { formatDateTime } from '../../utils/datetime';

interface LegalPersonalEntryFormConfirmType {
}

const LegalPersonalEntryFormConfirm: React.FC<LegalPersonalEntryFormConfirmType> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const entryFormInfo = useSelector((state: RootState) => state.entryForm);

  useEffect(() => {
    if(entryFormInfo.entryFormStep !== 'confirm') {
      dispatch(setField({field: 'entryFormStep', value: 'entry'}))
      navigate('/form/legalPersonal')
    }
  }, [entryFormInfo.entryFormStep])

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  type GenderKey = 'male' | 'female';

  const formatGender = (value: string) => {
    const genderMap: { [key in GenderKey]: string } = {
      'male': '男性',
      'female': '女性'
    };
  
    return genderMap[value as GenderKey] || '';
  }

  const handleNextPage = () => {
    dispatch(setField({field: 'entryFormStep', value: 'guide'}))
    navigate('/form/legalPersonal/guide');
  }

  const formatPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return '';

    // 携帯電話と固定電話で分岐
    if (phoneNumber.length === 11) {
        // 携帯電話の場合
        return phoneNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    } else {
        // 固定電話の場合
        return phoneNumber.replace(/(\d{2,4})(\d{2,4})(\d{4})/, '$1-$2-$3');
    }
  }


  return (
    <>
      <div className="bg-gray-50">
        <EntryFormHeader currentStep={2} />
        <main className="confirm-container">
          <div className="mb-8">
            <h1 className="text-2xl font-bold">入力内容の確認</h1>
            <p className="text-gray-600 mt-2">以下の内容をご確認ください。</p>
          </div>

          <section className="bg-white shadow rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">会社情報</h2>

            <div className="confirm-group">
              <div className="confirm-label">会社名</div>
              <div className="confirm-value" id="companyNameValue">{entryFormInfo.companyName}</div>
            </div>

            <div className="confirm-group">
              <div className="confirm-label">法人番号</div>
              <div className="confirm-value" id="corporateNumberValue">{entryFormInfo.corporateNumber.replace(/(\d{1})(\d{4})(\d{4})(\d{4})/, '$1-$2-$3-$4')}</div>
            </div>

            <div className="confirm-group">
              <div className="confirm-label">会社所在地</div>
              <div className="confirm-value" id="companyAddressValue">
                {`〒${entryFormInfo.companyPostalCode} ${entryFormInfo.companyPrefecture} ${entryFormInfo.companyCity} ${entryFormInfo.companyStreetAddress} ${entryFormInfo.companyBuilding}`}
              </div>
            </div>

            <div className="confirm-group">
              <div className="confirm-label">代表者名</div>
              <div className="confirm-value" id="representativeNameValue">
                {`${entryFormInfo.representativeLastName} ${entryFormInfo.representativeFirstName}`}
              </div>
            </div>
          </section>

          <section className="bg-white shadow rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">申請者情報</h2>
            <div className="bg-white shadow rounded-lg p-6 mb-8">
              <div className="confirm-group">
                <div className="confirm-label">氏名</div>
                <div className="confirm-value" id="fullNameValue">
                  {`${entryFormInfo.lastName} ${entryFormInfo.firstName}`}
                </div>
                <div className="text-sm text-gray-500" id="fullNameKanaValue">
                  {`${entryFormInfo.lastNameKa} ${entryFormInfo.firstNameKa}`}
                </div>
              </div>

              <div className="confirm-group">
                <div className="confirm-label">生年月日</div>
                <div className="confirm-value" id="birthDateValue">
                  {new Date(entryFormInfo.birthday).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  })}
                </div>
              </div>

              <div className="confirm-group">
                <div className="confirm-label">住所</div>
                <div className="confirm-value" id="addressValue">
                  {`〒${entryFormInfo.postalCode} ${entryFormInfo.prefecture} ${entryFormInfo.city} ${entryFormInfo.address} ${entryFormInfo.building}`}
                </div>
              </div>

              <div className="confirm-group">
                <div className="confirm-label">性別</div>
                <div className="confirm-value" id="genderValue">{formatGender(entryFormInfo.gender)}</div>
              </div>

              <div className="confirm-group">
                <div className="confirm-label">電話番号</div>
                <div className="confirm-value" id="phoneNumberValue">{formatPhoneNumber(entryFormInfo.phoneNum)}</div>
              </div>

              <div className="confirm-group">
                <div className="confirm-label">メールアドレス</div>
                <div className="confirm-value" id="emailValue">{entryFormInfo.email}</div>
              </div>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              type="button"
              id="backButton"
              className="btn-secondary"
              onClick={() => {
                dispatch(setField({field: 'entryFormStep', value: 'entry'}))
                navigate('/form/legalPersonal');
              }}
            >
              入力内容を修正する
            </button>
            <button
              type="button"
              id="nextButton"
              className="btn-primary"
              onClick={() => {
                handleNextPage();
              }}
            >
              法人確認に進む
            </button>
          </div>
        </main>
      </div>
    </>
  );
};

export default LegalPersonalEntryFormConfirm;
